import mime from 'mime';
import {
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from 'react';
import { useParams, useRouteMatch } from 'react-router';
import currentFileReducer, { ICurrentFileReducerState } from 'shared/Providers/CurrentFile/reducer';
import { useRecentContext } from 'shared/Providers/Recent';
import { useDB } from 'shared/Providers/UserDB';
import { addDocumentAttachment } from 'shared/Providers/UserDB/utils'
import CloudReader from 'shared/_model/cloud/CloudReader'
import { getFileThumbnailImage } from 'shared/_pages/video/utils'
import { TDocFile } from 'shared/constants/docs';
import { apiShareGetDocument } from 'shared/modules/share/api';

interface ICurrentFileContext extends ICurrentFileReducerState {
	readFile: () => Promise<TDocFile>;
	saveFile?: (values: Partial<TDocFile>) => Promise<TDocFile>;
}

const EMPTY_FILE = {} as TDocFile;

export type DocProviderStateType = 'loading' | 'loaded' | 'updating';

const initialReducerState: ICurrentFileReducerState = {
	status: 'loading',
	file: EMPTY_FILE,
	isNew: false,
};

export const CurrentFileContext = createContext<ICurrentFileContext>({
	...initialReducerState,
	isNew: true,
	readFile: () => Promise.resolve(EMPTY_FILE),
	saveFile: undefined,
	path: undefined,
});

interface ICurrentFileProviderProps extends PropsWithChildren {
	fileId: string;
}

export default function CurrentFileProvider({
	children,
	fileId,
}: ICurrentFileProviderProps): JSX.Element {
	const { sharingId } = useParams<{ sharingId?: string; fileId: string }>();
	const currentRoute = useRouteMatch();

	const [state, dispatch] = useReducer(currentFileReducer, initialReducerState);

	const { addRecent } = useRecentContext();
	const { readDoc, writeDoc } = useDB();

	const readFile = useCallback(async (): Promise<TDocFile> => {
		let f = EMPTY_FILE;
		if (fileId) {
			const getFileFunc = sharingId ? apiShareGetDocument : readDoc;
			f = (await getFileFunc(fileId, { attachments: true })) as TDocFile;
		}

		console.log('tst: context', 'readFile', f);
		// TODO: Old thumbnails are stored in cloud. We need to migrate them to attachments.
		const thumbnail = getFileThumbnailImage(f);
		if (thumbnail) {
			const url = await new CloudReader(thumbnail).getUrl();
			if (url) {
				try {
					const u = new URL(url);
					const contentType = mime.getType(u.pathname) || 'image/jpeg';
					const response = await fetch(url);
					let blob = await response.blob();
					if (blob.type !== contentType) {
						blob = new Blob([blob], { type: contentType });
					}

					if (blob) {
						// Hardcode to jpg because we don't know what we've uploaded before
						addDocumentAttachment(f, 'thumbnail', {
							content_type: contentType,
							data: blob,
						});
					}
				} catch (e) {
					console.error('tst: old thumbnail reading error', e);
				}
			}

			f.thumbnail = undefined;
			(f as any).image = undefined;
		}

		dispatch({ file: f, status: 'loaded' });

		return f;
	}, [sharingId, readDoc, fileId]);

	const saveFile = useCallback(
		async ({ _rev, ...values }: Partial<TDocFile>) => {
			if (!sharingId) {
				dispatch({ status: 'updating' });
				await writeDoc<TDocFile>({ _id: fileId, ...values });
				return readFile();
			}
			return EMPTY_FILE;
		},
		[sharingId, fileId, writeDoc, readFile]
	);

	const value = useMemo(
		() => ({
			...state,
			readFile,
			saveFile: sharingId ? undefined : saveFile,
		}),
		[state, sharingId, readFile, saveFile]
	);

	console.log('tst: context', value);

	useEffect(() => {
		void readFile().then((f) => {
			if (f?.type) {
				addRecent({
					type: f.type,
					title: f.title,
					to: currentRoute.url,
				});
			}
		});
	}, [addRecent, readFile, currentRoute.url]);

	return <CurrentFileContext.Provider value={value}>{children}</CurrentFileContext.Provider>;
}

export const useCurrentFile = (): ICurrentFileContext => useContext(CurrentFileContext);
